/**
 * Menu Drawer Submenu Animation
 *
 * Expand/collapse for the mobile drawer's nested <details> submenus, built on
 * the same pattern as the collapsible-content accordion in assets/global.js:
 * one Web Animations API effect owns height, padding and opacity together, and
 * CSS stays out of the way entirely (see the note above `.menu-drawer__submenu`
 * in src/css/components/component-menu-drawer.scss).
 *
 * The one structural difference from that accordion: it animates a plain
 * `<div open>`, so a MutationObserver on `open` can drive the whole thing.
 * These are real `<details>`, and the browser hides the content the instant
 * `open` goes away — so a collapse has to preventDefault the summary click,
 * animate, and only then drop the attribute. MenuDrawer.closeSubmenu() in
 * assets/global.js hands off here for exactly that reason.
 *
 * Ordering note: MenuDrawer binds its own summary click listener when the
 * custom element upgrades, and its handler reads `open` synchronously to decide
 * whether the click was an open or a close. That read has to happen before this
 * module flips the attribute — which holds because global.js and core.js are
 * both `defer` in layout/theme.liquid and global.js comes first in document
 * order. If those two ever swap, MenuDrawer would see the post-click state and
 * close a submenu 100ms after it opened.
 */

const DEFAULT_DURATION_MS = 300;
const DEFAULT_EASING = 'cubic-bezier(0.33, 1, 0.68, 1)';

// Opacity finishes ahead of the height: on the way in the links have settled
// before the box stops growing, on the way out they are gone before the gap
// closes. Without the stagger the fade reads as a dimmer rather than a reveal.
const FADE_IN_OFFSET = 0.6;
const FADE_OUT_OFFSET = 0.7;

const CLOSED_FRAME = { height: '0px', paddingTop: '0px', paddingBottom: '0px', opacity: '0' };

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

// submenu element -> { animation, direction }. Only populated while an
// animation is in flight, so `has()` doubles as "is this mid-transition".
const inFlight = new WeakMap();
const bound = new WeakSet();

function parseDuration(value, fallback) {
  const raw = value.trim();
  if (!raw) return fallback;
  const number = parseFloat(raw);
  if (!Number.isFinite(number)) return fallback;
  // Check 'ms' before 's' — '300ms' ends with both.
  return raw.endsWith('ms') ? number : number * 1000;
}

// Timing lives in CSS custom properties so the caret rotation in
// component-menu-drawer.scss and these keyframes can't drift apart.
function readTiming(submenu) {
  const styles = getComputedStyle(submenu);
  return {
    duration: parseDuration(styles.getPropertyValue('--submenu-duration'), DEFAULT_DURATION_MS),
    easing: styles.getPropertyValue('--submenu-easing').trim() || DEFAULT_EASING,
  };
}

function clearInlineStyles(submenu) {
  submenu.style.height = '';
  submenu.style.paddingTop = '';
  submenu.style.paddingBottom = '';
  submenu.style.opacity = '';
  submenu.style.overflow = '';
}

// Live rendered values, read before cancelling anything, so a reversal starts
// from where the eye last saw the element instead of snapping to an endpoint.
function currentFrame(submenu) {
  const styles = getComputedStyle(submenu);
  return {
    height: `${submenu.getBoundingClientRect().height}px`,
    paddingTop: styles.paddingTop,
    paddingBottom: styles.paddingBottom,
    opacity: styles.opacity,
  };
}

// Resting frame for the open state. Must run with `open` already set and with
// no animation in flight — a running WAAPI effect outranks inline styles, so
// measuring underneath one returns the animated value, not the natural one.
function openFrame(submenu) {
  const inline = submenu.style.cssText;
  clearInlineStyles(submenu);
  submenu.style.height = 'auto';

  const styles = getComputedStyle(submenu);
  const frame = {
    height: `${submenu.getBoundingClientRect().height}px`,
    paddingTop: styles.paddingTop,
    paddingBottom: styles.paddingBottom,
    opacity: '1',
  };

  submenu.style.cssText = inline;
  return frame;
}

function cancel(submenu) {
  const state = inFlight.get(submenu);
  if (!state) return;
  inFlight.delete(submenu);
  state.animation.cancel();
}

function setExpanded(details, expanded) {
  // Applied synchronously rather than on the animation's 100ms-late sibling in
  // MenuDrawer.onSummaryClick, so the caret (`[aria-expanded=true]` in CSS)
  // turns with the panel instead of after it.
  const summary = details.querySelector(':scope > summary');
  if (summary) summary.setAttribute('aria-expanded', String(expanded));
}

function run(submenu, from, to, direction, fadeTo, fadeOffset, onFinish) {
  const { duration, easing } = readTiming(submenu);
  submenu.style.overflow = 'hidden';

  const animation = submenu.animate(
    [
      { ...from, offset: 0 },
      { opacity: String(fadeTo), offset: fadeOffset },
      { ...to, offset: 1 },
    ],
    // `fill: 'both'` holds the final frame until the cleanup below runs, so
    // there's no bare frame between the effect ending and styles being reset.
    { duration, easing, fill: 'both' }
  );

  const state = { animation, direction };
  inFlight.set(submenu, state);

  animation.finished
    .catch(() => {})
    .then(() => {
      // Superseded by a newer toggle: that call owns the element now.
      if (inFlight.get(submenu) !== state) return;
      inFlight.delete(submenu);
      animation.cancel(); // drop the fill so CSS/inline styles take back over
      clearInlineStyles(submenu);
      onFinish();
    });
}

function expand(details) {
  const submenu = details.querySelector(':scope > .menu-drawer__submenu');
  if (!submenu) return;

  const state = inFlight.get(submenu);
  if (state && state.direction === 'expand') return;
  if (!state && details.hasAttribute('open')) return;

  const from = state ? currentFrame(submenu) : CLOSED_FRAME;
  cancel(submenu);

  // Set before measuring: `details[open] > .menu-drawer__submenu` supplies the
  // resting padding, and the content isn't laid out at all while closed.
  details.setAttribute('open', '');
  setExpanded(details, true);

  if (reducedMotion.matches) {
    clearInlineStyles(submenu);
    return;
  }

  run(submenu, from, openFrame(submenu), 'expand', 1, FADE_IN_OFFSET, () => {});
}

function collapse(details) {
  const submenu = details.querySelector(':scope > .menu-drawer__submenu');
  if (!submenu) return;

  const state = inFlight.get(submenu);
  if (state && state.direction === 'collapse') return;
  if (!state && !details.hasAttribute('open')) return;

  const from = currentFrame(submenu);
  cancel(submenu);
  setExpanded(details, false);

  if (reducedMotion.matches) {
    clearInlineStyles(submenu);
    details.removeAttribute('open');
    return;
  }

  run(submenu, from, CLOSED_FRAME, 'collapse', 0, FADE_OUT_OFFSET, () => {
    details.removeAttribute('open');
  });
}

function owns(details) {
  return details instanceof Element && !!details.querySelector(':scope > .menu-drawer__submenu');
}

function bind(details, submenu) {
  if (bound.has(details)) return;
  bound.add(details);

  const summary = details.querySelector(':scope > summary');
  if (!summary) return;

  summary.addEventListener('click', (event) => {
    // Always preventDefault: on collapse the attribute has to survive the
    // animation, and on expand we want the aria/caret state applied in the
    // same tick rather than waiting on the native toggle.
    event.preventDefault();
    if (details.hasAttribute('open')) collapse(details);
    else expand(details);
  });

  // Safety net for `open` being dropped from outside this module — chiefly
  // MenuDrawer.closeMenuDrawer(), which resets every submenu at once when the
  // whole drawer closes. The content is hidden the moment that happens, so
  // abandon the animation rather than let it finish against nothing.
  const observer = new MutationObserver(() => {
    if (details.hasAttribute('open')) return;

    if (inFlight.has(submenu)) {
      cancel(submenu);
      clearInlineStyles(submenu);
    }

    // closeMenuDrawer() clears `open` but never touches the submenu summaries,
    // so without this the caret stays rotated and the next time the drawer is
    // opened it points down over a collapsed submenu.
    setExpanded(details, false);
  });
  observer.observe(details, { attributes: true, attributeFilter: ['open'] });
}

function scan(root = document) {
  root.querySelectorAll('.menu-drawer__submenu').forEach((submenu) => {
    const details = submenu.parentElement;
    if (details && details.tagName === 'DETAILS') bind(details, submenu);
  });
}

function ready(fn) {
  if (document.readyState !== 'loading') fn();
  else document.addEventListener('DOMContentLoaded', fn, { once: true });
}

ready(() => {
  scan();
  // Header re-renders in the theme editor swap the drawer markup wholesale.
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
});

// Consumed by MenuDrawer.closeSubmenu() in assets/global.js, which is loaded
// separately from this bundle and must feature-detect before delegating.
window.MenuDrawerSubmenu = { expand, collapse, owns };
